/**
 * 🏗️ Глобальный store для заказов
 * Архитектура "Загрузил один раз - пользуйся всегда"
 * 
 * Принципы:
 * 1. Один запрос /orders при старте - загружаются ВСЕ заказы
 * 2. Мгновенная фильтрация без запросов к серверу
 * 3. Умные обновления через WebSocket
 * 4. Автоматический пересчет счетчиков
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { http } from '../api/http'

// Типы данных
export interface Order {
  id: number | string  // Поддерживаем оба типа для совместимости
  title: string
  status: string
  processing_types?: Array<{
    id: number
    name: string
    price?: string
    description?: string
  }>
  files?: any[]
  quantity?: number
  created_at?: string
  updated_at?: string
  price?: string
  urgent?: boolean
  company_name?: string
  deadline?: string
  description?: string
  preview_image?: string
  files_count?: number
  // Информация о поданной цене
  submitted_price?: number
  submitted_delivery_time?: number
  submitted_comment?: string
  // Информация о подтверждении
  confirmation_state?: 'pending' | 'accepted' | 'declined' | 'modified' | null
  confirmation_updated_at?: string
  // Информация о дедлайне
  completion_deadline?: string
  completion_deadline_formatted?: string
  deadline_status?: string
  deadline_css_class?: string
  days_remaining?: number
  confirmed_at?: string
  delivery_days?: number
  [key: string]: any
}

export interface OrderCounts {
  new: number
  watching: number
  calculating: number
  price_given: number
  pending_confirmation: number
  executing: number
  defect: number
  rejected: number
  archived: number
  completed: number
  // Счетчик непрочитанных сообщений чата
  unread_messages: number
}

export type OrderCategory = 'catalog' | 'views' | 'inwork' | 'archive'
export type OrderSubTab = 'new' | 'watching' | 'calculating' | 'price_given' | 'executing' | 'defect' | 'rejected' | 'archived' | 'completed'

// Маппинг подвкладок к статусам (из текущего проекта)
export const STATUS_MAP: Record<OrderSubTab, string> = {
  new: 'new',
  watching: 'watching',
  calculating: 'calculating',
  price_given: 'price_given,pending_confirmation', // Объединенные статусы
  executing: 'executing',
  defect: 'defect',
  rejected: 'rejected',
  archived: 'archived',
  completed: 'completed'
}

// Конфигурация категорий (из текущего проекта)
export const CATEGORY_CONFIG = {
  catalog: {
    title: 'Каталог',
    icon: '📋',
    subTabs: [
      { key: 'new' as OrderSubTab, title: 'Новые', icon: '🆕' }
    ]
  },
  views: {
    title: 'Просмотры',
    icon: '👀',
    subTabs: [
      { key: 'watching' as OrderSubTab, title: 'Просмотр', icon: '👁️' },
      { key: 'calculating' as OrderSubTab, title: 'На просчете', icon: '🧮' },
      { key: 'price_given' as OrderSubTab, title: 'Оцененные', icon: '💰' }
    ]
  },
  inwork: {
    title: 'В работе',
    icon: '⚙️',
    subTabs: [
      { key: 'executing' as OrderSubTab, title: 'В работе', icon: '🔧' },
      { key: 'defect' as OrderSubTab, title: 'Брак', icon: '❌' }
    ]
  },
  archive: {
    title: 'Архив',
    icon: '📦',
    subTabs: [
      { key: 'rejected' as OrderSubTab, title: 'Отказ', icon: '🚫' },
      { key: 'archived' as OrderSubTab, title: 'Архив', icon: '📁' },
      { key: 'completed' as OrderSubTab, title: 'Выполнено', icon: '✅' }
    ]
  }
}

// Детальная информация о заказе (расширенная версия Order)
export interface OrderDetail extends Order {
  contact_name?: string
  comments?: Array<{
    id: number
    text: string
    created_at: string
    author: string
  }>
  // Дополнительные поля, которые есть только в детальном представлении
  full_description?: string
  detailed_files?: Array<{
    id: number
    name: string
    url: string
    type: string
    size?: number
  }>
}

// Интерфейс состояния store
interface OrdersStoreState {
  // Данные
  allOrders: Order[]
  counts: OrderCounts
  
  // 🆕 Кеш детальной информации о заказах
  orderDetailsCache: Map<string, OrderDetail>
  
  // 🔥 СОСТОЯНИЕ ЧАТА
  isChatWidgetActive: boolean
  activeChatId: string | null
  
  // Состояние загрузки
  isLoading: boolean
  isInitialized: boolean
  error: string | null
  lastUpdated: Date | null
  
  // Действия
  loadAllOrders: () => Promise<void>
  updateOrderInGlobalCache: (orderId: string, updates: Partial<Order>) => void
  addOrderToGlobalCache: (order: Order) => void
  removeOrderFromGlobalCache: (orderId: string) => void
  updateCountsFromWebSocket: (newCounts: OrderCounts) => void
  updateUnreadMessagesCount: (count: number) => void
  setChatWidgetActive: (active: boolean) => void
  setActiveChatId: (chatId: string | null) => void
  
  // 🆕 Методы для работы с кешем деталей заказов
  getOrderDetailFromCache: (orderId: string) => OrderDetail | null
  addOrderDetailToCache: (orderId: string, orderDetail: OrderDetail) => void
  updateOrderDetailInCache: (orderId: string, updates: Partial<OrderDetail>) => void
  removeOrderDetailFromCache: (orderId: string) => void
  clearOrderDetailsCache: () => void
  
  // Вычисляемые значения (мгновенная фильтрация)
  getOrdersByCategory: (category: OrderCategory, subTab: OrderSubTab, filters?: {
    processing_types?: string[]
    excluded_types?: string[]
    search?: string
    sort?: 'newest' | 'oldest' | 'title_asc' | 'title_desc' | 'company_asc' | 'company_desc'
  }) => Order[]
  
  // Пересчет счетчиков из кеша
  recalculateCounts: () => OrderCounts
}

/**
 * 🔄 Функция фильтрации заказов по категории/подвкладке
 * Работает МГНОВЕННО с уже загруженными данными
 * Поддерживает все фильтры и сортировки из useOrderFiltersState
 */
export function filterOrdersByCategory(
  allOrders: Order[], 
  category: OrderCategory, 
  subTab: OrderSubTab,
  filters?: {
    processing_types?: string[]
    excluded_types?: string[]
    search?: string
    sort?: 'newest' | 'oldest' | 'title_asc' | 'title_desc' | 'company_asc' | 'company_desc'
  }
): Order[] {
  // 1. Получаем статусы для подвкладки
  const targetStatuses = STATUS_MAP[subTab].split(',')
  
  // 2. Фильтруем по статусу
  let filteredOrders = allOrders.filter(order => 
    targetStatuses.includes(order.status)
  )
  
  // 3. Применяем дополнительные фильтры
  if (filters) {
    // Фильтр по типам обработки
    if (filters.processing_types && filters.processing_types.length > 0) {
      filteredOrders = filteredOrders.filter(order => 
        order.processing_types?.some(type => 
          filters.processing_types!.includes(type.name)
        )
      )
    }
    
    // Исключение типов обработки (но НЕ исключаем выбранные в фильтрах)
    if (filters.excluded_types && filters.excluded_types.length > 0) {
      const selectedTypes = filters.processing_types || []
      filteredOrders = filteredOrders.filter(order => 
        !order.processing_types?.some(type => 
          filters.excluded_types!.includes(type.name) && !selectedTypes.includes(type.name)
        )
      )
    }
    
    // Поиск по тексту
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filteredOrders = filteredOrders.filter(order => 
        order.title?.toLowerCase().includes(searchLower) ||
        order.description?.toLowerCase().includes(searchLower) ||
        order.company_name?.toLowerCase().includes(searchLower)
      )
    }
  }
  
  // 4. Сортировка с поддержкой всех типов из useOrderFiltersState
  const sortType = filters?.sort || 'newest'
  
  return filteredOrders.sort((a, b) => {
    switch (sortType) {
      case 'oldest':
        const dateA_old = new Date(a.updated_at || a.created_at || 0)
        const dateB_old = new Date(b.updated_at || b.created_at || 0)
        return dateA_old.getTime() - dateB_old.getTime()
        
      case 'title_asc':
        return (a.title || '').localeCompare(b.title || '', 'ru')
        
      case 'title_desc':
        return (b.title || '').localeCompare(a.title || '', 'ru')
        
      case 'company_asc':
        return (a.company_name || '').localeCompare(b.company_name || '', 'ru')
        
      case 'company_desc':
        return (b.company_name || '').localeCompare(a.company_name || '', 'ru')
        
      case 'newest':
      default:
        const dateA_new = new Date(a.updated_at || a.created_at || 0)
        const dateB_new = new Date(b.updated_at || b.created_at || 0)
        return dateB_new.getTime() - dateA_new.getTime()
    }
  })
}

/**
 * 📊 Пересчет счетчиков из кеша заказов
 */
export function calculateCountsFromOrders(allOrders: Order[]): OrderCounts {
  const counts: OrderCounts = {
    new: 0,
    watching: 0,
    calculating: 0,
    price_given: 0,
    pending_confirmation: 0,
    executing: 0,
    defect: 0,
    rejected: 0,
    archived: 0,
    completed: 0,
    unread_messages: 0 // Будет сохранен из текущего состояния
  }
  
  allOrders.forEach(order => {
    const status = order.status as keyof OrderCounts
    if (status in counts) {
      counts[status]++
    }
  })
  
  // Объединяем price_given и pending_confirmation для подвкладки "Оцененные"
  counts.price_given = counts.price_given + counts.pending_confirmation
  
  return counts
}

/**
 * 💬 Вспомогательная функция для сохранения счетчика чата при пересчете
 */
function preserveChatCount(newCounts: OrderCounts, currentCounts: OrderCounts): OrderCounts {
  return {
    ...newCounts,
    unread_messages: currentCounts.unread_messages
  }
}

/**
 * 📊 Пересчет счетчиков с учетом фильтров (для каталога)
 * Применяет фильтры к заказам и пересчитывает счетчики
 */
export function calculateFilteredCounts(
  allOrders: Order[],
  filters?: {
    processing_types?: string[]
    excluded_types?: string[]
    search?: string
  }
): OrderCounts {
  // Если нет фильтров, возвращаем обычные счетчики
  if (!filters || (!filters.processing_types?.length && !filters.excluded_types?.length && !filters.search)) {
    return calculateCountsFromOrders(allOrders)
  }
  
  // Применяем фильтры к заказам
  let filteredOrders = [...allOrders]
  
  // Фильтр по типам обработки
  if (filters.processing_types && filters.processing_types.length > 0) {
    filteredOrders = filteredOrders.filter(order => 
      order.processing_types?.some(type => 
        filters.processing_types!.includes(type.name)
      )
    )
  }
  
  // Исключение типов обработки (но НЕ исключаем выбранные в фильтрах)
  if (filters.excluded_types && filters.excluded_types.length > 0) {
    const selectedTypes = filters.processing_types || []
    filteredOrders = filteredOrders.filter(order => 
      !order.processing_types?.some(type => 
        filters.excluded_types!.includes(type.name) && !selectedTypes.includes(type.name)
      )
    )
  }
  
  // Поиск по тексту
  if (filters.search) {
    const searchLower = filters.search.toLowerCase()
    filteredOrders = filteredOrders.filter(order => 
      order.title?.toLowerCase().includes(searchLower) ||
      order.description?.toLowerCase().includes(searchLower) ||
      order.company_name?.toLowerCase().includes(searchLower)
    )
  }
  
  // Пересчитываем счетчики для отфильтрованных заказов
  return calculateCountsFromOrders(filteredOrders)
}

/**
 * 🏪 Создание глобального store заказов
 */
export const useOrdersStore = create<OrdersStoreState>()(
  subscribeWithSelector((set, get) => ({
    // Начальное состояние
    allOrders: [],
    counts: {
      new: 0,
      watching: 0,
      calculating: 0,
      price_given: 0,
      pending_confirmation: 0,
      executing: 0,
      defect: 0,
      rejected: 0,
      archived: 0,
      completed: 0,
      unread_messages: 0
    },
    // 🆕 Инициализация кеша деталей заказов
    orderDetailsCache: new Map<string, OrderDetail>(),
    
    // 🔥 СОСТОЯНИЕ ЧАТА
    isChatWidgetActive: false,
    activeChatId: null,
    
    isLoading: false,
    isInitialized: false,
    error: null,
    lastUpdated: null,
    
    /**
     * 🚀 Загрузка ВСЕХ заказов одним запросом
     */
    loadAllOrders: async () => {
      const state = get()
      
      // Предотвращаем повторную загрузку
      if (state.isLoading || state.isInitialized) {
        logger.log('📋 Заказы уже загружены или загружаются')
        return
      }
      
      set({ isLoading: true, error: null })
      
      try {
        // Запрос БЕЗ фильтров - получаем ВСЕ заказы пользователя
        const response = await http.get('/orders', {
          params: {
            // Можно добавить limit если нужно ограничить количество
            // limit: 1000,
            sort_by: 'newest'
          }
        })
        
        if (response.data.success) {
          const allOrders = response.data.orders || []
          
          // Пересчитываем счетчики из загруженных данных
          const calculatedCounts = calculateCountsFromOrders(allOrders)
          
          // 🔥 ИСПРАВЛЕНИЕ: НЕ загружаем счетчик чатов здесь - это делает useChatMessagesSimple
          // Счетчик чатов будет обновлен при загрузке чатов в ChatWidget
          calculatedCounts.unread_messages = 0
          
          set({
            allOrders,
            counts: calculatedCounts,
            isLoading: false,
            isInitialized: true,
            error: null,
            lastUpdated: new Date()
          })
          

        } else {
          throw new Error(response.data.message || 'Ошибка загрузки заказов')
        }
      } catch (error: any) {
        logger.error('❌ Ошибка загрузки заказов:', error)
        set({
          isLoading: false,
          error: error.message || 'Ошибка загрузки заказов'
        })
      }
    },
    
    /**
     * 🔄 Обновление конкретного заказа в глобальном кеше
     * Вызывается из WebSocket событий
     */
    updateOrderInGlobalCache: (orderId: string, updates: Partial<Order>) => {
      set(state => {
        // Ищем по строковому и числовому ID для совместимости
        const orderIndex = state.allOrders.findIndex(order => 
          String(order.id) === String(orderId)
        )
        
        if (orderIndex === -1) {
          return state
        }
        
        const updatedOrders = [...state.allOrders]
        updatedOrders[orderIndex] = {
          ...updatedOrders[orderIndex],
          ...updates,
          updated_at: new Date().toISOString()
        }
        
        // Пересчитываем счетчики с сохранением счетчика чата
        const newCounts = preserveChatCount(
          calculateCountsFromOrders(updatedOrders),
          state.counts
        )
        
        return {
          ...state,
          allOrders: updatedOrders,
          counts: newCounts,
          lastUpdated: new Date()
        }
      })
    },
    
    /**
     * ➕ Добавление нового заказа в глобальный кеш
     */
    addOrderToGlobalCache: (order: Order) => {
      set(state => {
        // Проверяем, что заказ еще не существует
        const exists = state.allOrders.some(existing => 
          String(existing.id) === String(order.id)
        )
        if (exists) {
          return state
        }
        
        const updatedOrders = [order, ...state.allOrders]
        const newCounts = preserveChatCount(
          calculateCountsFromOrders(updatedOrders),
          state.counts
        )
        
        return {
          ...state,
          allOrders: updatedOrders,
          counts: newCounts,
          lastUpdated: new Date()
        }
      })
    },
    
    /**
     * ➖ Удаление заказа из глобального кеша
     */
    removeOrderFromGlobalCache: (orderId: string) => {
      set(state => {
        const updatedOrders = state.allOrders.filter(order => 
          String(order.id) !== String(orderId)
        )
        const newCounts = preserveChatCount(
          calculateCountsFromOrders(updatedOrders),
          state.counts
        )
        
        return {
          ...state,
          allOrders: updatedOrders,
          counts: newCounts,
          lastUpdated: new Date()
        }
      })
    },
    
    /**
     * 📊 Обновление счетчиков из WebSocket
     */
    updateCountsFromWebSocket: (newCounts: OrderCounts) => {
      set(state => ({
        ...state,
        counts: newCounts,
        lastUpdated: new Date()
      }))
    },

    /**
     * 💬 Обновление счетчика непрочитанных сообщений чата
     */
    updateUnreadMessagesCount: (count: number) => {
      set(state => ({
        ...state,
        counts: {
          ...state.counts,
          unread_messages: count
        },
        lastUpdated: new Date()
      }))
    },

    // 🔥 УПРАВЛЕНИЕ СОСТОЯНИЕМ ЧАТА
    setChatWidgetActive: (active: boolean) => {
      set(state => ({
        ...state,
        isChatWidgetActive: active
      }))
    },
    setActiveChatId: (chatId: string | null) => {
      set(state => ({
        ...state,
        activeChatId: chatId
      }))
    },
    
    /**
     * ⚡ Мгновенная фильтрация заказов по категории/подвкладке
     * НЕТ запросов к серверу - только работа с кешем!
     */
    getOrdersByCategory: (category: OrderCategory, subTab: OrderSubTab, filters?) => {
      const state = get()
      return filterOrdersByCategory(state.allOrders, category, subTab, filters)
    },
    
    /**
     * 🔢 Пересчет счетчиков из текущего кеша
     */
    recalculateCounts: () => {
      const state = get()
      const newCounts = calculateCountsFromOrders(state.allOrders)
      
      // 🔥 СОХРАНЯЕМ СЧЕТЧИК ЧАТА при пересчете
      const countsWithChat = {
        ...newCounts,
        unread_messages: state.counts.unread_messages
      }
      
      set(prevState => ({
        ...prevState,
        counts: countsWithChat
      }))
      
      return countsWithChat
    },
    
    /**
     * 🔍 Получение детальной информации о заказе из кеша
     */
    getOrderDetailFromCache: (orderId: string) => {
      const state = get()
      return state.orderDetailsCache.get(orderId) || null
    },
    
    /**
     * ➕ Добавление детальной информации о заказе в кеш
     */
    addOrderDetailToCache: (orderId: string, orderDetail: OrderDetail) => {
      set(state => {
        const newCache = new Map(state.orderDetailsCache)
        newCache.set(orderId, orderDetail)
        
        logger.log(`📦 Заказ #${orderId} добавлен в кеш деталей`)
        
        return {
          ...state,
          orderDetailsCache: newCache,
          lastUpdated: new Date()
        }
      })
    },
    
    /**
     * 🔄 Обновление детальной информации о заказе в кеше
     */
    updateOrderDetailInCache: (orderId: string, updates: Partial<OrderDetail>) => {
      set(state => {
        const existingDetail = state.orderDetailsCache.get(orderId)
        if (!existingDetail) {
          return state // Не обновляем если заказа нет в кеше
        }
        
        const newCache = new Map(state.orderDetailsCache)
        const updatedDetail = {
          ...existingDetail,
          ...updates,
          updated_at: new Date().toISOString()
        }
        newCache.set(orderId, updatedDetail)
        
        logger.log(`🔄 Детали заказа #${orderId} обновлены в кеше`)
        
        return {
          ...state,
          orderDetailsCache: newCache,
          lastUpdated: new Date()
        }
      })
    },
    
    /**
     * ➖ Удаление детальной информации о заказе из кеша
     */
    removeOrderDetailFromCache: (orderId: string) => {
      set(state => {
        const newCache = new Map(state.orderDetailsCache)
        const removed = newCache.delete(orderId)
        
        if (removed) {
          logger.log(`🗑️ Заказ #${orderId} удален из кеша деталей`)
        }
        
        return {
          ...state,
          orderDetailsCache: newCache,
          lastUpdated: new Date()
        }
      })
    },
    
    /**
     * 🧹 Очистка всего кеша деталей заказов
     */
    clearOrderDetailsCache: () => {
      set(state => {
        logger.log('🧹 Кеш деталей заказов очищен')
        
        return {
          ...state,
          orderDetailsCache: new Map<string, OrderDetail>(),
          lastUpdated: new Date()
        }
      })
    }
  }))
)

/**
 * 🎯 Хук для получения отфильтрованных заказов (заменяет useOrders)
 * Поддерживает все фильтры и сортировки из useOrderFiltersState
 */
export function useFilteredOrders(
  category: OrderCategory, 
  subTab: OrderSubTab,
  filters?: {
    processing_types?: string[]
    excluded_types?: string[]
    search?: string
    sort?: 'newest' | 'oldest' | 'title_asc' | 'title_desc' | 'company_asc' | 'company_desc'
  }
) {
  const { allOrders, isLoading, error, isInitialized, getOrdersByCategory } = useOrdersStore()
  
  // Мгновенная фильтрация без запросов к серверу
  const filteredOrders = isInitialized ? getOrdersByCategory(category, subTab, filters) : []
  
  return {
    orders: filteredOrders,
    isLoading,
    error,
    isInitialized,
    total: filteredOrders.length
  }
}

/**
 * 📊 Хук для получения счетчиков (заменяет useOrderCounters)
 */
export function useOrderCounters() {
  const { counts, isLoading, error, isInitialized } = useOrdersStore()
  
  return {
    counts,
    isLoading,
    error,
    isInitialized
  }
}

/**
 * 📊 Хук для получения отфильтрованных счетчиков (для каталога)
 */
export function useFilteredOrderCounters(
  filters?: {
    processing_types?: string[]
    excluded_types?: string[]
    search?: string
  }
) {
  const { allOrders, isLoading, error, isInitialized } = useOrdersStore()
  
  // Мгновенный пересчет счетчиков с учетом фильтров
  const filteredCounts = useMemo(() => {
    if (!isInitialized || !allOrders.length) {
      return {
        new: 0,
        watching: 0,
        calculating: 0,
        price_given: 0,
        pending_confirmation: 0,
        executing: 0,
        defect: 0,
        rejected: 0,
        archived: 0,
        completed: 0
      }
    }
    
    return calculateFilteredCounts(allOrders, filters)
  }, [allOrders, filters, isInitialized])
  
  return {
    counts: filteredCounts,
    isLoading,
    error,
    isInitialized
  }
}

/**
 * 🚀 Хук для инициализации store при запуску приложения
 */
import { useEffect, useMemo } from 'react'
import { logger } from '../utils/logger'

export function useInitializeOrders() {
  const { loadAllOrders, isInitialized, isLoading } = useOrdersStore()
  
  // Загружаем заказы при первом рендере
  useEffect(() => {
    if (!isInitialized && !isLoading) {
      loadAllOrders()
    }
  }, [loadAllOrders, isInitialized, isLoading])
  
  return { isInitialized, isLoading }
}

/**
 * 🎯 Хук для работы с кешированными деталями заказа
 * Проверяет кеш перед загрузкой с сервера
 */
export function useCachedOrderDetail(orderId: string) {
  const { 
    getOrderDetailFromCache, 
    addOrderDetailToCache, 
    updateOrderDetailInCache 
  } = useOrdersStore()
  
  return {
    // Получение из кеша
    getCachedOrder: () => getOrderDetailFromCache(orderId),
    
    // Добавление в кеш
    cacheOrder: (orderDetail: OrderDetail) => addOrderDetailToCache(orderId, orderDetail),
    
    // Обновление в кеше
    updateCachedOrder: (updates: Partial<OrderDetail>) => updateOrderDetailInCache(orderId, updates),
    
    // Проверка наличия в кеше
    isInCache: () => getOrderDetailFromCache(orderId) !== null
  }
}
